import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../environments/environment";
import {
  PaginatedResponse,
  Pagination,
} from "../interfaces/pagination.interface";
import {
  ActivityAttachment,
  ActivityComment,
  ActivityType,
  ProjectActivity,
} from "../interfaces/project-activity.interface";

@Injectable({
  providedIn: "root",
})
export class ActivityService {
  constructor(private http: HttpClient) {}

  getProjectActivities(
    projectId: string,
    pagination: Pagination,
  ): Observable<PaginatedResponse<ProjectActivity>> {
    return this.http.get<PaginatedResponse<ProjectActivity>>(
      `${environment.API_URL}projects/${projectId}/activities`,
      { params: { ...pagination } },
    );
  }

  getLatestActivitiesForProject(
    projectId: string,
    limit: number,
  ): Observable<ProjectActivity[]> {
    return this.http.get<ProjectActivity[]>(
      `${environment.API_URL}projects/${projectId}/versions/activities/latest`,
      { params: { limit } },
    );
  }

  createNewActivity(
    projectId: string,
    newActivity: {
      activityType: ActivityType;
      description: string;
      projectVersionId?: string;
    },
  ): Observable<ProjectActivity> {
    return this.http.post<ProjectActivity>(
      `${environment.API_URL}projects/${projectId}/activities`,
      newActivity,
    );
  }

  editActivity(
    projectId: string,
    activityId: string,
    activity: ProjectActivity,
  ): Observable<ProjectActivity> {
    return this.http.put<ProjectActivity>(
      `${environment.API_URL}projects/${projectId}/activities/${activityId}`,
      activity,
    );
  }

  postComment(
    projectId: string,
    activityId: string,
    commentText: string,
  ): Observable<ActivityComment> {
    return this.http.post<ActivityComment>(
      `${environment.API_URL}projects/${projectId}/activities/${activityId}/comments`,
      { commentText },
    );
  }

  editComment(
    projectId: string,
    activityId: string,
    commentId: string,
    commentText: string,
  ): Observable<ActivityComment> {
    return this.http.put<ActivityComment>(
      `${environment.API_URL}projects/${projectId}/activities/${activityId}/comments/${commentId}`,
      { commentText },
    );
  }

  deleteComment(
    projectId: string,
    activityId: string,
    commentId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${projectId}/activities/${activityId}/comments/${commentId}`,
    );
  }

  uploadAttachment(
    projectId: string,
    activityId: string,
    file: File,
  ): Observable<ActivityAttachment> {
    const formData = new FormData();
    formData.append("file", file);
    return this.http.post<ActivityAttachment>(
      `${environment.API_URL}projects/${projectId}/activities/${activityId}/attachments`,
      formData,
    );
  }

  deleteAttachment(
    projectId: string,
    activityId: string,
    attachmentId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${projectId}/activities/${activityId}/attachments/${attachmentId}`,
    );
  }
}
